import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListViewsResult,
  type SavedView,
  type ViewDefinition,
} from '@whinerp/client';
import { Button, Field, InlineAction, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * The saved-view bar above a list (F-021).
 *
 * Three things the feature's own brief asks for, in order of how often they
 * bite:
 *
 * Filter chips are individually removable. A filter panel that needs a drawer
 * opened to drop one condition is a daily friction point, and this is the one
 * place that shows.
 *
 * An unsaved change says so, and offers save, save-as and discard - because
 * the common case is somebody adjusting a shared view they may not edit, and
 * "save as" is the only honest thing to offer them.
 *
 * A view somebody else owns is read-only, and says why rather than silently
 * failing to save.
 */
export interface ViewColumnChoice {
  /** The column key a view stores. */
  key: string;
  label: string;
}

export interface ViewGroupChoice {
  /** The sortable field key a view stores as `groupBy`. */
  field: string;
  label: string;
}

export function ViewBar({
  objectKey,
  active,
  draft,
  columns,
  groupable,
  onSelect,
  onChange,
  onRemoveFilter,
  onDiscard,
}: {
  objectKey: string;
  active: SavedView | null;
  /** The definition on screen, which may differ from what was saved. */
  draft: ViewDefinition;
  /**
   * Every column this list can show, in its default order.
   *
   * Passed in rather than read from the server, because the labels are the
   * ones the table's own headers use - a picker that named columns differently
   * from the headers it controls would be a second vocabulary for one thing.
   */
  columns: ViewColumnChoice[];
  /** The fields this list can group by. Empty hides the control entirely. */
  groupable: ViewGroupChoice[];
  onSelect: (view: SavedView | null) => void;
  /** A change to the definition on screen: columns, grouping. */
  onChange: (next: ViewDefinition) => void;
  onRemoveFilter: (index: number) => void;
  onDiscard: () => void;
}): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [picking, setPicking] = useState(false);

  const views = useQuery<ListViewsResult, ApiError>({
    queryKey: ['views', objectKey],
    queryFn: () => api.listViews({ path: { objectKey } }),
  });

  const save = useMutation({
    mutationFn: () =>
      api.updateView({
        path: { viewId: active!.viewId },
        body: {
          name: active!.name,
          definition: draft,
          visibility: active!.visibility,
          teamId: active!.teamId,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['views', objectKey] });
      onDiscard();
    },
  });

  const saveAs = useMutation({
    mutationFn: () =>
      api.createView({
        path: { objectKey },
        body: { name, definition: draft, visibility: 'private' },
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['views', objectKey] });
      setNaming(false);
      setName('');
      onSelect(created);
    },
  });

  const pin = useMutation({
    mutationFn: (next: boolean) =>
      api.pinView({ path: { viewId: active!.viewId }, body: { pinned: next } }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['views', objectKey] }),
  });

  // A skeleton on error is a spinner that never resolves. The list below it
  // works without a view bar, so the bar simply is not there.
  if (views.isPending) return <Skeleton className="h-input w-full" />;
  if (views.isError) return <></>;
  const available = views.data.views;

  const conditions = readConditions(draft);
  const dirty = active !== null && JSON.stringify(draft) !== JSON.stringify(active.definition);

  /*
   * Which columns are ticked.
   *
   * An empty stored set means "the list's own default", not "no columns" - the
   * server reads it the same way - so the picker shows every box ticked rather
   * than none, which is what somebody looking at a full table would expect.
   */
  const chosen = draft.columns?.filter((key) => columns.some((c) => c.key === key)) ?? [];
  const shown = chosen.length > 0 ? chosen : columns.map((column) => column.key);

  const toggleColumn = (key: string): void => {
    const next = shown.includes(key) ? shown.filter((k) => k !== key) : [...shown, key];
    /*
     * The last column cannot be unticked.
     *
     * A view with no columns renders a table of nothing, which reads as "no
     * records" and is not. The server falls back to the full set for exactly
     * this reason; refusing here means the person sees why rather than
     * watching their choice quietly not take.
     */
    if (next.length === 0) return;
    onChange({ ...draft, columns: next });
  };

  return (
    <div data-component="ViewBar" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/*
          * The picker only where there is something to pick.
          *
          * With no saved views it was a select holding one option - "All
          * records" - which cannot change anything and tells whoever opens it
          * that this is all there is. The rest of the bar still draws, so a
          * view can still be saved from what is on screen.
          */}
        {available.length > 0 ? (
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('view.switch')}
            <select
              data-view-select
              value={active?.viewId ?? ''}
              onChange={(event) => {
                const found = available.find((v) => v.viewId === event.currentTarget.value);
                onSelect(found ?? null);
              }}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            >
              <option value="">{t('view.all')}</option>
              {available.map((view) => (
                <option key={view.viewId} value={view.viewId}>
                  {view.pinned ? '★ ' : ''}
                  {view.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {columns.length > 0 ? (
          <InlineAction data-view-columns onClick={() => setPicking((open) => !open)}>
            {t('view.columns.chosen', {
              values: { shown: String(shown.length), total: String(columns.length) },
            })}
          </InlineAction>
        ) : null}

        {groupable.length > 0 ? (
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('view.groupBy')}
            <select
              data-view-group
              value={draft.groupBy ?? ''}
              onChange={(event) => {
                const field = event.currentTarget.value;
                const { groupBy: _dropped, ...rest } = draft;
                void _dropped;
                onChange(field === '' ? rest : { ...rest, groupBy: field });
              }}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            >
              <option value="">{t('view.groupBy.none')}</option>
              {groupable.map((choice) => (
                <option key={choice.field} value={choice.field}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {active ? (
          <>
            <InlineAction
              data-view-pin={active.viewId}
              onClick={() => pin.mutate(!active.pinned)}
            >
              {active.pinned ? t('view.unpin') : t('view.pin')}
            </InlineAction>
            {!active.canEdit ? (
              <span data-view-read-only className="text-meta text-ink-meta">
                {t('view.readOnly')}
              </span>
            ) : null}
          </>
        ) : null}
      </div>

      {picking ? (
        <fieldset
          data-view-column-picker
          className="flex flex-wrap gap-3 rounded-control border border-control px-3 py-2"
        >
          <legend className="px-1 text-meta text-ink-meta">{t('view.columns')}</legend>
          {columns.map((column) => (
            <label key={column.key} className="flex items-center gap-1.5 text-dense text-ink-600">
              <input
                type="checkbox"
                data-view-column={column.key}
                checked={shown.includes(column.key)}
                onChange={() => toggleColumn(column.key)}
                className="h-icon-inline w-icon-inline rounded-control border-control"
              />
              {column.label}
            </label>
          ))}
        </fieldset>
      ) : null}

      {/*
        One chip per condition, each with its own remove. The brief calls a
        panel that needs opening to drop one condition a daily friction point,
        and it is right.
      */}
      {conditions.length > 0 ? (
        <ul data-view-chips className="flex flex-wrap gap-2">
          {conditions.map((condition, index) => (
            <li
              key={`${condition.field}:${String(index)}`}
              data-view-chip={condition.field}
              className="flex items-center gap-1 rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600"
            >
              <span>
                {condition.field} {condition.operator} {String(condition.value ?? '')}
              </span>
              <button
                type="button"
                data-view-chip-remove={condition.field}
                aria-label={`${t('list.removeFilter', { values: { filter: condition.field } })}`}
                onClick={() => onRemoveFilter(index)}
                className="text-ink-400 hover:text-ink"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {dirty ? (
        <div data-view-unsaved className="flex flex-wrap items-center gap-2 rounded-control bg-warning-tint px-2 py-1.5">
          <span className="text-dense text-ink">{t('view.unsaved')}</span>
          {active?.canEdit ? (
            <Button intent="secondary" size="small" data-view-save busy={save.isPending} onClick={() => save.mutate()}>
              {t('view.save')}
            </Button>
          ) : null}
          <Button intent="ghost" size="small" data-view-save-as onClick={() => setNaming(true)}>
            {t('view.saveAs')}
          </Button>
          <Button intent="ghost" size="small" data-view-discard onClick={onDiscard}>
            {t('view.discard')}
          </Button>
        </div>
      ) : null}

      {naming ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('view.name')}
            data-view-name
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <Button
            intent="primary"
            size="small"
            data-view-save-as-confirm
            busy={saveAs.isPending}
            disabled={name.trim() === ''}
            onClick={() => saveAs.mutate()}
          >
            {t('view.save')}
          </Button>
        </div>
      ) : null}

      {save.isError || saveAs.isError ? (
        <p data-view-refusal className="text-dense text-danger-text">
          {errorMessage(save.error ?? saveAs.error!)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The top-level conditions of a filter, as chips.
 *
 * Only the outermost `all` group: a nested any/none is a shape a chip cannot
 * honestly represent, and showing it as a flat chip somebody can remove would
 * change the filter's meaning rather than narrow it.
 */
function readConditions(
  definition: ViewDefinition,
): Array<{ field: string; operator: string; value?: unknown }> {
  const filter = definition.filter as { all?: unknown[] } | undefined;
  if (!filter || !Array.isArray(filter.all)) return [];
  return filter.all.filter(
    (node): node is { field: string; operator: string; value?: unknown } =>
      typeof node === 'object' && node !== null && 'field' in node,
  );
}
